import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";
import { field, forward } from "../field.js";

const MASK_LABEL = { show: "Show masked content", hide: "Hide masked content" };
const MASK_MESSAGE = { masked: "Input content is hidden", visible: "Input content is visible" };
const COPY_LABEL = "Copy masked content";
const COPY_ICON = { idle: "clipboard-copy", success: "clipboard-checked", error: "clipboard-x" };
const COPY_MESSAGE = { idle: "", success: "Copied to clipboard", error: "Failed to copy to clipboard" };

/**
 * <i80-masked-input label="Team token" name="token" value="036215df…" copy-button></i80-masked-input>
 *
 * A secret in a real <input> (or <textarea> with multiline): the value is hidden until the
 * user reveals it with the toggle button, and can be copied with the copy button. Because
 * the control is a native one, the form submits the value as usual. An <input> or
 * <textarea> written inside the tag is kept, so server-rendered forms plug in:
 *   <i80-masked-input label="Token" copy-button>{{ form.token }}</i80-masked-input>
 */
export class I80MaskedInput extends I80Element {
  static get observedAttributes() {
    return ["label", "helper-text", "error", "invalid", "required", "optional", "disabled", "readonly",
      "visible", "copy-button", "width", "height"];
  }

  setup() {
    let control = this.part("control") ?? this.querySelector("textarea, input");
    const multiline = this.flag("multiline") || control?.localName === "textarea";
    if (!control) {
      control = multiline ? h("textarea", { rows: "4" }) : h("input", { type: "text" });
      forward(this, control);
      if (multiline) {
        control.removeAttribute("value");
        if (this.hasAttribute("value")) control.value = this.getAttribute("value");
      }
    }
    control.setAttribute("data-i80-part", "control");
    this._control = control;
    this._multiline = multiline;

    const box = control.closest(".i80-form-masked-input") ?? h("div", { class: "i80-form-masked-input" }, control);
    this._box = box;
    this._masked = !this.flag("visible");

    this._toggle = box.querySelector(".i80-form-masked-input__toggle-button")
      ?? h("button", { class: "i80-form-visibility-toggle i80-form-masked-input__toggle-button", type: "button" });
    this._toggle.addEventListener("click", () => {
      this._masked = !this._masked;
      this._toggled = true;
      this.update();
      this.dispatchEvent(new CustomEvent("i80-visibility-change", { bubbles: true, detail: { masked: this._masked } }));
    });
    if (!this._toggle.isConnected) control.after(this._toggle);

    this._render = field(this, control, { controlWrapper: box });
    this._toggle.setAttribute("aria-controls", control.id);
  }

  adopt() {
    // A streamed response can append the server's own control after the element was built:
    // that one is the author's, so it takes the place of the one we created.
    const late = this.takeContent().find((n) => n.nodeType === Node.ELEMENT_NODE && ["input", "textarea"].includes(n.localName));
    if (!late) return;
    this._control.replaceWith(late);
    late.setAttribute("data-i80-part", "control");
    this._control = late;
    this._multiline = late.localName === "textarea";
    this._render = field(this, late, { controlWrapper: this._box });
    this._toggle.setAttribute("aria-controls", late.id);
  }

  /** The copy button is a real <button> next to the control, copying the control's value. */
  _copyButton() {
    if (!this.flag("copy-button")) {
      this._copy?.remove();
      this._copyMessage?.remove();
      this._copy = this._copyMessage = null;
      return;
    }
    if (!this._copy) {
      this._copy = this._box.querySelector(".i80-form-masked-input__copy-button") ?? h("button", { type: "button" });
      this._copyMessage = this._box.querySelector(".i80-form-masked-input__copy-message")
        ?? h("span", { class: "sr-only i80-form-masked-input__copy-message", "aria-live": "polite" });
      this._copy.addEventListener("click", async () => {
        let status = "success";
        try {
          await navigator.clipboard.writeText(this._control.value);
        } catch {
          status = "error";
        }
        this._copyStatus(status);
        this.dispatchEvent(new CustomEvent("i80-copy", { bubbles: true, detail: { ok: status === "success", text: this._control.value } }));
      });
      this._box.append(this._copy, this._copyMessage);
    }
    this._copyStatus(this._status ?? "idle", false);
  }

  _copyStatus(status, reset = true) {
    this._status = status;
    const c = this._copy;
    if (!c) return;
    c.className = `i80-button i80-button--color-secondary i80-button--is-icon-only i80-button--size-medium i80-copy-button i80-copy-button--status-${status} i80-form-masked-input__copy-button`;
    c.setAttribute("aria-label", COPY_LABEL);
    c.replaceChildren(h("span", { class: "i80-button__icon" }, icon(COPY_ICON[status], { stretched: true })));
    this._copyMessage.textContent = COPY_MESSAGE[status];
    clearTimeout(this._timer);
    // Fade back to the default state, as the Ember copy button does.
    if (reset && status !== "idle") this._timer = setTimeout(() => this._copyStatus("idle", false), 1500);
  }

  update() {
    const control = this._control;
    if (this.hasAttribute("required")) control.required = this.flag("required");
    if (this.hasAttribute("disabled")) control.disabled = this.flag("disabled");
    if (this.hasAttribute("readonly")) control.readOnly = this.flag("readonly");
    // Once the user has used the toggle, the attribute no longer drives the state.
    if (this.hasAttribute("visible") && !this._toggled) this._masked = !this.flag("visible");
    const { invalid } = this._render();

    this._box.className = `i80-form-masked-input i80-form-masked-input--is-${this._masked ? "" : "not-"}masked`;
    this._box.style.width = this.getAttribute("width") ?? "";
    control.className = [
      this._multiline ? "i80-form-textarea" : "i80-form-text-input",
      "i80-typography-body-200", "i80-font-weight-regular",
      invalid && (this._multiline ? "i80-form-textarea--is-invalid" : "i80-form-text-input--is-invalid"),
      "i80-form-masked-input__control",
    ].filter(Boolean).join(" ");
    if (this._multiline) control.style.height = this.getAttribute("height") ?? "";

    this._toggle.setAttribute("aria-label", this._masked ? MASK_LABEL.show : MASK_LABEL.hide);
    this._toggle.replaceChildren(
      icon(this._masked ? "eye" : "eye-off"),
      h("span", { class: "sr-only", "aria-live": "polite" }, this._masked ? MASK_MESSAGE.masked : MASK_MESSAGE.visible),
    );
    this._copyButton();
  }

  /** Whether the content is hidden; setting it moves the toggle. */
  get masked() {
    return this._masked;
  }

  set masked(v) {
    this._masked = !!v;
    this._toggled = true;
    this.update();
  }

  get value() {
    return this._control?.value ?? this.getAttribute("value") ?? "";
  }

  set value(v) {
    if (this._control) this._control.value = v;
    else this.setAttribute("value", v);
  }

  focus(options) {
    this._control?.focus(options);
  }
}

define("i80-masked-input", I80MaskedInput);
